/**
 * EmptyState — placeholder padronizado para telas que ainda não têm dado real
 * (integração pendente, feature em construção, filtro sem resultados).
 *
 * Uso:
 *   <EmptyState
 *     title="Aguardando integração"
 *     description="A tela vai encher assim que o coletor entrar no ar."
 *     hint="Sem previsão."
 *     icon={<GaugeIcon />}
 *   />
 *
 * Segue o design system: usa .nx-card por baixo, tokens de cor/espaço, dá
 * suporte a light/dark mode via variáveis --nx-*.
 */
import type { ReactNode } from 'react';

export interface EmptyStateProps {
  title: string;
  description?: string;
  hint?: string;
  icon?: ReactNode;
  /** Ações opcionais (botões) mostradas abaixo da descrição. */
  actions?: ReactNode;
}

function DefaultIcon() {
  // Placeholder "medidor": mostrador circular com ponteiro.
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle
        cx="24"
        cy="24"
        r="19"
        stroke="currentColor"
        strokeWidth="2"
        strokeOpacity="0.35"
      />
      <path
        d="M9 30a15 15 0 0 1 30 0"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M24 24 33 15"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <circle cx="24" cy="24" r="2.5" fill="currentColor" />
    </svg>
  );
}

export function EmptyState({ title, description, hint, icon, actions }: EmptyStateProps) {
  return (
    <div
      className="nx-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: 'var(--nx-space-10) var(--nx-space-6)',
        gap: 'var(--nx-space-3)',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 72,
          height: 72,
          borderRadius: 'var(--nx-radius-pill)',
          background: 'var(--nx-bg-alt)',
          border: '1px solid var(--nx-line)',
          color: 'var(--nx-mute-strong)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 'var(--nx-space-2)',
        }}
      >
        {icon ?? <DefaultIcon />}
      </div>
      <h2 style={{ fontSize: 'var(--nx-text-lg)', margin: 0 }}>{title}</h2>
      {description && (
        <p
          style={{
            color: 'var(--nx-mute-strong)',
            maxWidth: 520,
            margin: 0,
            fontSize: 'var(--nx-text-base)',
          }}
        >
          {description}
        </p>
      )}
      {hint && (
        <p
          style={{
            color: 'var(--nx-mute)',
            fontSize: 'var(--nx-text-sm)',
            margin: 0,
            maxWidth: 520,
          }}
        >
          {hint}
        </p>
      )}
      {actions && <div style={{ marginTop: 'var(--nx-space-2)' }}>{actions}</div>}
    </div>
  );
}

export default EmptyState;
